import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  fetchPurchaseOrderAttachmentUrl,
  payPurchaseOrder,
} from 'api/purchaseOrder';
import { toCostNumber } from 'components/modules/CreatePurchaseOrderModal/CreatePurchaseOrderModal.utils';
import { getAttachmentDisplayName } from 'utils/s3';

import {
  formatPayAmount,
  getPayPurchaseOrderDescription,
  PayPurchaseOrderModalProps,
} from './PayPurchaseOrderModal.props';

const PayPurchaseOrderModalView: FC<PayPurchaseOrderModalProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  onPurchaseOrderUpdated,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const attachments = purchaseOrder?.attachments ?? [];
  const manufacturerName = purchaseOrder?.manufacturer?.name ?? '';
  const [subtotal, setSubtotal] = useState('0');
  const [shippingCost, setShippingCost] = useState('0');
  const [tax, setTax] = useState('0');
  const [costAdjustment, setCostAdjustment] = useState('0');
  const [description, setDescription] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [previewKey, setPreviewKey] = useState<string>();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSubtotal(String(toCostNumber(purchaseOrder?.subtotal)));
    setShippingCost(String(toCostNumber(purchaseOrder?.shippingCost)));
    setTax(String(toCostNumber(purchaseOrder?.tax)));
    setCostAdjustment(String(toCostNumber(purchaseOrder?.costAdjustment)));
    setDescription(
      getPayPurchaseOrderDescription(purchaseOrder?.poNumer, manufacturerName),
    );
    setSelectedKeys(attachments);
    setPreviewKey(attachments[0]);
  }, [isOpen, purchaseOrder?._id]);

  const {
    data: previewUrl,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery(
    ['purchase-order-pay-attachment-url', purchaseOrder?._id, previewKey],
    () =>
      fetchPurchaseOrderAttachmentUrl(
        purchaseOrder?._id || '',
        previewKey || '',
      ),
    {
      enabled: Boolean(isOpen && purchaseOrder?._id && previewKey),
      staleTime: 4 * 60 * 1000,
    },
  );

  const parsedSubtotal = toCostNumber(subtotal);
  const parsedShippingCost = toCostNumber(shippingCost);
  const parsedTax = toCostNumber(tax);
  const parsedCostAdjustment = toCostNumber(costAdjustment);
  const total =
    parsedSubtotal + parsedShippingCost + parsedTax + parsedCostAdjustment;

  const { mutate: submitPayment, isLoading: isPaying } = useMutation(
    () =>
      payPurchaseOrder(purchaseOrder?._id || '', {
        subtotal: parsedSubtotal,
        shippingCost: parsedShippingCost,
        tax: parsedTax,
        costAdjustment: parsedCostAdjustment,
        description: description.trim(),
        attachmentKeys: selectedKeys,
      }),
    {
      onSuccess: (data) => {
        if (data?.item) {
          onPurchaseOrderUpdated?.(data.item);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title: 'Vendor payment submitted',
          isClosable: true,
          duration: 4000,
        });
        onClose();
      },
      onError: (error: any) => {
        toast({
          status: 'error',
          title: 'Failed to pay vendor',
          description:
            error?.response?.data?.message ||
            error?.message ||
            'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="10px" maxW="1100px" maxH="90vh">
        <ModalCloseButton isDisabled={isPaying} _focus={{ outline: 'none' }} />
        <ModalHeader mt={4} textAlign="center">
          Pay Vendor
          {purchaseOrder?.poNumer ? ` - ${purchaseOrder.poNumer}` : ''}
        </ModalHeader>
        <ModalBody pb={4}>
          <Text mb={4}>
            Confirm you want to pay {manufacturerName || 'this vendor'}.
          </Text>

          <Flex gap={3} mb={4} wrap="wrap">
            <FormControl maxW="180px">
              <FormLabel fontSize="12px" color="#afafaf">
                Subtotal
              </FormLabel>
              <Input
                type="number"
                value={subtotal}
                isDisabled={isPaying}
                onChange={(event) => setSubtotal(event.target.value)}
              />
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel fontSize="12px" color="#afafaf">
                Shipping Cost
              </FormLabel>
              <Input
                type="number"
                value={shippingCost}
                isDisabled={isPaying}
                onChange={(event) => setShippingCost(event.target.value)}
              />
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel fontSize="12px" color="#afafaf">
                Tax
              </FormLabel>
              <Input
                type="number"
                value={tax}
                isDisabled={isPaying}
                onChange={(event) => setTax(event.target.value)}
              />
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel fontSize="12px" color="#afafaf">
                Cost Adjustment
              </FormLabel>
              <Input
                type="number"
                value={costAdjustment}
                isDisabled={isPaying}
                onChange={(event) => setCostAdjustment(event.target.value)}
              />
            </FormControl>
          </Flex>

          <Text fontWeight="semibold" mb={4}>
            Total: {formatPayAmount(total)}
          </Text>

          <FormControl mb={4} isRequired>
            <FormLabel fontSize="12px" color="#afafaf">
              Description
            </FormLabel>
            <Textarea
              value={description}
              isDisabled={isPaying}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormControl>

          <Text fontSize="12px" color="#afafaf" mb={2}>
            Attachments
          </Text>
          <Flex gap={6} minH="360px" align="stretch">
            <Flex
              w="280px"
              flexShrink={0}
              direction="column"
              gap={3}
              overflowY="auto"
              pr={1}>
              {attachments.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  No attachments available
                </Text>
              ) : (
                attachments.map((attachment) => {
                  const isSelected = attachment === previewKey;
                  const isChecked = selectedKeys.includes(attachment);

                  return (
                    <Box
                      key={attachment}
                      p={3}
                      cursor="pointer"
                      border="1px solid"
                      borderColor={isSelected ? 'primary.500' : 'gray.200'}
                      bg={isSelected ? '#83FF9C80' : 'white'}
                      borderRadius="8px"
                      onClick={() => setPreviewKey(attachment)}>
                      <Checkbox
                        isChecked={isChecked}
                        isDisabled={isPaying}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedKeys((current) => [
                              ...current,
                              attachment,
                            ]);
                          } else {
                            setSelectedKeys((current) =>
                              current.filter((key) => key !== attachment),
                            );
                          }
                        }}>
                        {getAttachmentDisplayName(attachment)}
                      </Checkbox>
                    </Box>
                  );
                })
              )}
            </Flex>

            <Box
              flex={1}
              minH="360px"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              overflow="hidden"
              bg="gray.50">
              {isPreviewLoading ? (
                <Center h="100%">
                  <Spinner />
                </Center>
              ) : isPreviewError ? (
                <Center h="100%">
                  <Text color="gray.500">Unable to load this attachment.</Text>
                </Center>
              ) : previewUrl ? (
                <iframe
                  title="Purchase order payment attachment preview"
                  src={previewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', minHeight: '360px' }}
                />
              ) : (
                <Center h="100%">
                  <Text color="gray.500">Select an attachment to preview</Text>
                </Center>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            isDisabled={isPaying}
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={isPaying}
            isDisabled={!description.trim() || !purchaseOrder?._id}
            onClick={() => submitPayment()}>
            Pay Vendor
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PayPurchaseOrderModalView;
